import { useEffect, useState } from "react"

/** How long a screen will wait on artwork before rendering without it. */
const PRELOAD_TIMEOUT_MS = 4000

/**
 * Fetches and decodes a set of images and reports when they are all in the
 * browser's cache, so a screen can hold a loading state until the artwork it is
 * about to animate is actually there.
 *
 * The point is the animation, not the load. A background-image that arrives
 * mid-flight pops in halfway through its own entrance — the card slides in as an
 * empty tinted rectangle and fills a beat later, which reads as broken rather
 * than as loading. Waiting first and mounting once means every frame of the
 * animation has something to show.
 *
 * It never hangs and never fails: a 404, a decode error or a slow connection all
 * resolve exactly like a hit, capped at `PRELOAD_TIMEOUT_MS`. Decoration must not
 * be able to lock a guest out of a workshop, and the family tint behind each card
 * is a deliberate fallback for the image never arriving.
 *
 * `sources` must be a stable reference — a module constant or a `useMemo` — since
 * it is an effect dependency; a fresh array each render would restart the fetch
 * forever.
 */
export function usePreloadedImages(sources: readonly string[], enabled = true): boolean {
  // Nothing to wait for is answered by the return expression, not by a state
  // write, so a disabled caller is ready on its first render.
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!enabled || sources.length === 0) return

    let live = true
    let outstanding = sources.length
    const settle = () => {
      if (!live) return
      outstanding -= 1
      if (outstanding === 0) setLoaded(true)
    }

    for (const src of sources) {
      const image = new Image()
      image.src = src
      /*
       * `decode()` waits for the pixels to be ready, not just the bytes — a
       * `load` alone can still be followed by a decode on the frame that paints
       * it. It rejects on a broken image, which lands in the same `settle`.
       */
      if (typeof image.decode === "function") {
        void image.decode().then(settle, settle)
      } else {
        image.addEventListener("load", settle)
        image.addEventListener("error", settle)
      }
    }

    const timeout = window.setTimeout(() => {
      if (live) setLoaded(true)
    }, PRELOAD_TIMEOUT_MS)

    return () => {
      live = false
      window.clearTimeout(timeout)
    }
  }, [sources, enabled])

  return loaded || !enabled || sources.length === 0
}
